import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Input } from 'designSystem/components/Input'
import { Spinner } from 'designSystem/components/Spinner'
import { DocumentPrintAction } from 'features/knowledgeDocumentViewer/components/toolbar/PrintDocumentButton'
import { SearchResultsNavigator } from 'features/knowledgeDocumentViewer/components/toolbar/SearchResultsNavigator'
import { CitedTextBar } from 'features/knowledgeDocumentViewer/components/viewer/search/CitedTextBar'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useSearchActions } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import { useGeckoNotifications } from 'features/notifications'
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Link,
  Search,
  Star,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { cmmsNativeLink } from './cmmsNativeLink'
import { CmmsScrollLightbox } from './CmmsScrollLightbox'
import { CmmsScrollViewer } from './CmmsScrollViewer'
import { useWorkOrderPdf } from './useWorkOrderPdf'
import { cmmsRecordNumber, workOrderRowFromEntry } from './workOrder'
import { WorkOrderDownloadButton } from './WorkOrderDownloadButton'
import {
  findWorkOrderMatches,
  locateCmmsCitation,
  locateWorkOrderCitation,
} from './workOrderHighlights'
import {
  WorkOrderCanvas,
  WorkOrderEmpty,
  WorkOrderNotice,
  WorkOrderRoot,
  WorkOrderSearch,
  WorkOrderTitle,
  WorkOrderToolbar,
} from './workOrderViewerStyles'

const EMPTY_QUOTES: string[] = []

export function WorkOrderViewer({
  documentId,
  initialPage = 1,
  onPageChange,
  citedQuotes = EMPTY_QUOTES,
  expectedVersion,
  expectedDocumentRevision,
  citedNodeIds,
  onDismissCitedQuotes,
  fullTab = false,
  isActive = true,
}: {
  documentId: string
  initialPage?: number
  onPageChange?: (page: number) => void
  citedQuotes?: string[]
  expectedVersion?: string
  expectedDocumentRevision?: string
  citedNodeIds?: string[]
  onDismissCitedQuotes?: () => void
  fullTab?: boolean
  isActive?: boolean
}) {
  const { t } = useLingui()
  const { entry, pdf, pdfUrl, isLoading, error, retry } =
    useWorkOrderPdf(documentId)
  const nativeLink = entry ? cmmsNativeLink(entry) : undefined
  const { canGoBack, canGoForward, goBack, goForward, activeTabId } =
    useKnowledgeDocumentUXContext()
  const { siteID, orgID } = useKnowledgeDocumentDataContext()
  const { isFavorited, toggleFavorite, favoritesDisabled, favoritesSaving } =
    useSearchActions()
  const { successNotification, errorNotification } = useGeckoNotifications()
  const [page, setPage] = useState(initialPage)
  const [fullscreen, setFullscreen] = useState(false)
  const framedRequest = useRef<string>()
  const viewport = useRef<{ page: number; fraction: number } | null>(null)
  const [findOpen, setFindOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeHit, setActiveHit] = useState(0)
  const [dismissed, setDismissed] = useState(false)
  const [focusRequest, setFocusRequest] = useState<{
    id: number
    kind: 'citation' | 'search'
    index: number
  }>({ id: 0, kind: 'citation', index: 0 })
  const stale = Boolean(
    (entry &&
      expectedVersion &&
      entry.data.source.version !== expectedVersion) ||
    (entry &&
      expectedDocumentRevision &&
      entry.data.document?.revision !== expectedDocumentRevision)
  )
  const citedRuns = useMemo(
    () =>
      stale || !pdf
        ? []
        : expectedDocumentRevision && citedNodeIds?.length
          ? citedQuotes.flatMap((quote) =>
              locateCmmsCitation(pdf.runs, pdf.evidence, quote, citedNodeIds)
            )
          : citedQuotes.flatMap((quote) =>
              locateWorkOrderCitation(pdf.runs, pdf.evidence, quote)
            ),
    [pdf, citedQuotes, stale, expectedDocumentRevision, citedNodeIds]
  )
  const searchHits = useMemo(
    () => (pdf ? findWorkOrderMatches(pdf.runs, query) : []),
    [pdf, query]
  )
  const hit = searchHits[activeHit]
  const totalPages = pdf?.pageCount ?? 1
  const currentPage = Math.min(Math.max(page, 1), totalPages)
  const highlightedRuns = useMemo(
    () => (findOpen && query ? (hit ?? []) : dismissed ? [] : citedRuns),
    [findOpen, query, hit, dismissed, citedRuns]
  )

  useEffect(() => {
    setPage(initialPage)
  }, [documentId, initialPage])
  useEffect(() => {
    setDismissed(false)
    setFindOpen(false)
    setFocusRequest((request) => ({
      id: request.id + 1,
      kind: 'citation',
      index: 0,
    }))
  }, [documentId, citedQuotes])
  useEffect(() => {
    if (!isActive) setFullscreen(false)
  }, [isActive])
  useEffect(() => {
    if (!isActive || !fullTab) return
    const keydown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'f') {
        event.preventDefault()
        setFindOpen(true)
      }
    }
    document.addEventListener('keydown', keydown)
    return () => document.removeEventListener('keydown', keydown)
  }, [fullTab, isActive])

  const navigatePage = useCallback(
    (nextPage: number) => {
      setPage(nextPage)
      onPageChange?.(nextPage)
    },
    [onPageChange]
  )
  const closeFullscreen = useCallback(() => setFullscreen(false), [])
  const openFullscreen = useCallback(() => setFullscreen(true), [])
  const dismissQuotes = () => {
    setDismissed(true)
    onDismissCitedQuotes?.()
  }
  const changeQuery = (value: string) => {
    setQuery(value)
    setActiveHit(0)
    setFocusRequest((request) => ({
      id: request.id + 1,
      kind: 'search',
      index: 0,
    }))
  }
  const nextHit = (direction: number) => {
    if (!searchHits.length) return
    const next = (activeHit + direction + searchHits.length) % searchHits.length
    setActiveHit(next)
    setFocusRequest((request) => ({
      id: request.id + 1,
      kind: 'search',
      index: next,
    }))
  }
  const favorite = () => {
    if (entry) toggleFavorite(workOrderRowFromEntry(entry))
  }
  const copyLink = async () => {
    const url = new URL(window.location.href)
    url.search = new URLSearchParams({
      orgId: orgID,
      siteId: siteID,
      documentID: documentId,
      pageNumber: String(currentPage),
    }).toString()
    try {
      await navigator.clipboard.writeText(url.toString())
      successNotification(t`Link copied`)
    } catch {
      errorNotification(t`Could not copy link`)
    }
  }
  const viewerProps = {
    pdfUrl: pdfUrl ?? '',
    alt:
      entry?.data.document?.title ||
      entry?.data.record.description ||
      t`CMMS record`,
    currentPage,
    totalPages,
    highlights: highlightedRuns,
    focusTarget:
      focusRequest.kind === 'citation'
        ? !dismissed
          ? citedRuns[0]
          : undefined
        : findOpen
          ? searchHits[focusRequest.index]?.[0]
          : undefined,
    focusRequestId: `${documentId}:${focusRequest.id}`,
    viewport,
    framedRequest,
    onPageChange: navigatePage,
  }

  return (
    <WorkOrderRoot>
      <WorkOrderToolbar>
        {fullTab ? (
          <>
            <Button
              variant='ghost'
              size='icon'
              aria-label={t`Back`}
              disabled={!canGoBack}
              onClick={() => goBack(activeTabId)}>
              <ArrowLeft />
            </Button>
            <Button
              variant='ghost'
              size='icon'
              aria-label={t`Forward`}
              disabled={!canGoForward}
              onClick={() => goForward(activeTabId)}>
              <ArrowRight />
            </Button>
          </>
        ) : null}
        <WorkOrderTitle>
          {entry ? cmmsRecordNumber(entry) : t`CMMS record`}
          <span>{entry?.data.source.system.toUpperCase()}</span>
        </WorkOrderTitle>
        {nativeLink ? (
          <Button asChild variant='ghost' size='icon' title={t`Open in Maximo`}>
            <a
              href={nativeLink}
              target='_blank'
              rel='noopener noreferrer'
              aria-label={t`Open in Maximo`}>
              <ExternalLink />
            </a>
          </Button>
        ) : null}
        {fullTab ? <SearchResultsNavigator /> : null}
        <Button
          variant='ghost'
          size='icon'
          title={t`Find in document`}
          aria-label={t`Find in document`}
          onClick={() => setFindOpen((open) => !open)}>
          <Search />
        </Button>
        <Button
          variant='ghost'
          size='icon'
          title={t`Copy link`}
          aria-label={t`Copy link`}
          onClick={copyLink}>
          <Link />
        </Button>
        <Button
          variant='ghost'
          size='icon'
          title={
            isFavorited(documentId)
              ? t`Remove from favorites`
              : t`Add to favorites`
          }
          aria-label={
            isFavorited(documentId)
              ? t`Remove from favorites`
              : t`Add to favorites`
          }
          aria-pressed={isFavorited(documentId)}
          onClick={favorite}
          disabled={!entry || favoritesDisabled}
          aria-busy={favoritesSaving}>
          <Star fill={isFavorited(documentId) ? 'currentColor' : 'none'} />
        </Button>
        {fullTab ? (
          <DocumentPrintAction
            signedUrl={pdfUrl}
            totalPages={totalPages}
            currentPageNumber={currentPage}
            documentType='pdf'
            size='icon'
          />
        ) : null}
        <WorkOrderDownloadButton
          documentId={documentId}
          size='icon'
          showPageMenu={fullTab}
          currentPage={currentPage}
        />
      </WorkOrderToolbar>
      {findOpen ? (
        <WorkOrderSearch role='search'>
          <Input
            autoFocus
            inputSize='toolbar'
            aria-label={t`Find in document`}
            placeholder={t`Find in document...`}
            value={query}
            onChange={(event) => changeQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setFindOpen(false)
              if (event.key === 'Enter') nextHit(event.shiftKey ? -1 : 1)
            }}
          />
          <span>
            {searchHits.length
              ? `${activeHit + 1} / ${searchHits.length}`
              : query
                ? t`No results`
                : ''}
          </span>
          <Button
            size='icon'
            variant='ghost'
            aria-label={t`Previous match`}
            disabled={!searchHits.length}
            onClick={() => nextHit(-1)}>
            <ChevronUp />
          </Button>
          <Button
            size='icon'
            variant='ghost'
            aria-label={t`Next match`}
            disabled={!searchHits.length}
            onClick={() => nextHit(1)}>
            <ChevronDown />
          </Button>
          <Button
            size='icon'
            variant='ghost'
            aria-label={t`Close search`}
            onClick={() => setFindOpen(false)}>
            <X />
          </Button>
        </WorkOrderSearch>
      ) : null}
      {stale ? (
        <WorkOrderNotice role='status'>
          <Trans>
            This CMMS record has changed since it was cited. Showing the latest
            imported record.
          </Trans>
        </WorkOrderNotice>
      ) : null}
      {error || (!isLoading && !entry) ? (
        <WorkOrderEmpty role='alert'>
          <Trans>
            This CMMS record is unavailable or you do not have access.
          </Trans>
          <Button onClick={() => void retry()}>
            <Trans>Retry</Trans>
          </Button>
        </WorkOrderEmpty>
      ) : !pdfUrl ? (
        <WorkOrderEmpty role='status'>
          <Spinner />
          <Trans>Preparing CMMS record…</Trans>
        </WorkOrderEmpty>
      ) : (
        <WorkOrderCanvas>
          {citedQuotes.length && !dismissed && !findOpen ? (
            <CitedTextBar
              isLocating={false}
              isLocated={citedRuns.length > 0}
              onDismiss={dismissQuotes}
              placement='top-left'
            />
          ) : null}
          <CmmsScrollViewer
            key={pdfUrl}
            {...viewerProps}
            onToggleFullscreen={openFullscreen}
            isActive={isActive && !fullscreen}
          />
        </WorkOrderCanvas>
      )}
      {fullscreen && isActive ? (
        <CmmsScrollLightbox {...viewerProps} onClose={closeFullscreen} />
      ) : null}
    </WorkOrderRoot>
  )
}
